/**
 * ReportFileSelector - 报告文件选择器组件
 * 用于选择当前查看的报告文件
 */
import React from 'react';
import { isEmpty } from 'lodash-es';
import moment from 'moment';
import { Dropdown, List } from 'tea-component';
import classNames from 'classnames';
import EllipsisHover from '@gp/components/EllipsisHover';
import BasicImage from '@home/components/BasicImage';
import { IReportFileSelectorProps, IReportFileItem } from '../types';
import { REPORT_STEP_TYPE } from '../constants';
import defaultFileIcon from '@/pages/aigc/assets/svg/agent-document-file.svg';
import defaultFile from '@/pages/aigc/assets/svg/agent-ds-temp-file.svg';
import './ReportFileSelector.scss';

export const ReportFileSelector: React.FC<IReportFileSelectorProps> = ({
  fileList,
  currentFile,
  activeFileKey,
  containerWidth,
  activeStep,
  triggerTitleMaxWidthPx,
  onFileSelect,
  triggerIcon,
  className,
  overlayClassName,
  itemIconRenderer,
}) => {
  const list = fileList?.documents;

  if (isEmpty(list)) {
    return null;
  }

  const renderMenu = (close: () => void) => (
    <List type="option">
      {list?.map((item: IReportFileItem) => {
        // 获取列表项图标：优先使用自定义渲染函数，否则使用默认图标
        const itemIcon = itemIconRenderer ? itemIconRenderer(item) : defaultFileIcon;
        return (
          <List.Item
            onClick={() => onFileSelect(item, close)}
            key={item.id}
            selected={activeFileKey === item.id + item.version}
          >
            <div className="dba-report-file-selector__item">
              <div className="dba-report-file-selector__item-icon">
                <BasicImage src={itemIcon} errorSrc={defaultFileIcon} />
              </div>
              <div
                className="dba-report-file-selector__item-info"
                style={{ maxWidth: `${containerWidth - 120}px` }}
              >
                <EllipsisHover text={item.display_title || item.title} />
                <span className="dba-report-file-selector__item-date">
                  {moment(item.date_time).utcOffset(+8).format('YYYY-MM-DD HH:mm')}
                </span>
              </div>
            </div>
          </List.Item>
        );
      })}
    </List>
  );

  /**
   * 触发器标题最大宽度：优先使用调用方固定值（如 AI Gallery 双 Tab 同宽），
   * 否则沿用 DS 报告 / 魔法海报按步骤推导。
   */
  const maxTitleWidth =
    typeof triggerTitleMaxWidthPx === 'number' &&
    !Number.isNaN(triggerTitleMaxWidthPx)
      ? triggerTitleMaxWidthPx
      : containerWidth - (activeStep !== REPORT_STEP_TYPE.REPORT ? 100 : 520);

  return (
    <div
      className={classNames('dba-report-file-selector', className)}
      // 阻止拖拽冒泡
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Dropdown
        trigger="hover"
        clickClose={false}
        style={{ marginRight: 10 }}
        overlayClassName={classNames('dba-report-file-selector-overlay', overlayClassName)}
        button={
          <div className="dba-report-file-selector__trigger">
            <img
              src={triggerIcon || defaultFile}
              alt=""
              className="dba-report-file-selector__trigger-icon"
            />
            <div
              className="dba-report-file-selector__trigger-title"
              style={{ maxWidth: `${maxTitleWidth}px` }}
            >
              <EllipsisHover
                text={currentFile?.display_title || currentFile?.title || currentFile?.name ||  ''}
              />
            </div>
          </div>
        }
      >
        {renderMenu}
      </Dropdown>
    </div>
  );
};

export default ReportFileSelector;
